import { ReactNode } from "react";
import {
  Table as TanstackTable,
  SortingState,
  HeaderGroup,
  GroupingState,
  Row,
} from "@tanstack/react-table";
import {
  Box,
  Table as MuiTable,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  type SxProps,
} from "@mui/material";
import { Theme } from "@mui/material/styles";

import { TableLoader } from "ui/data-grid/components/table-loader/table-loader";
import { TableColumnsGroup } from "ui/data-grid/components/table-view/table-columns-group";
import { TableHeaderCell } from "ui/data-grid/components/table-view/table-header-cell";
import { TableBodyContent } from "ui/data-grid/components/table-view/table-body-content";
import { useTableLayout } from "ui/data-grid/components/table-view/use-table-layout";

import { mergeSx } from "ui/merge-sx";

import "ui/data-grid/data-grid.types";
import { EmptyGroupMeta, TableStyles } from "ui/data-grid/data-grid.types";

const DEFAULT_SKELETON_ROW_COUNT = 5;

const fixedTableSx = (theme: Theme) => ({
  tableLayout: "fixed",
  borderCollapse: "separate",
  borderSpacing: 0,
  "& .MuiTableCell-root": {
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
});

const CONTAINER_SX: SxProps<Theme> = (theme) => ({
  position: "relative",
  display: "flex",
  flexDirection: "column",
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: 1,
  overflowX: "auto",
  overflowY: "hidden",
});

/**
 * `flex: 0 1 auto` and not `flex: 1`: the bordered box may shrink to whatever height the page offers
 * it, but it must never grow past its own rows, or a three-record table draws a screen-tall frame
 * (#37752). The `min-height: 0` pair repeats down to the scrolling body for two reasons — a
 * `flex-basis: 0` descendant collapses a content-sized ancestor to nothing, and the shrink has to
 * reach the body for a table taller than the page to keep scrolling under its fixed head.
 */
const CONTAINER_FILL_HEIGHT_SX: SxProps<Theme> = { flex: "0 1 auto", minHeight: 0, minWidth: 0 };

const TABLE_WRAPPER_SX: SxProps<Theme> = {
  display: "flex",
  width: "100%",
  flexDirection: "column",
};

const TABLE_WRAPPER_FILL_HEIGHT_SX: SxProps<Theme> = { flex: "0 1 auto", minHeight: 0 };

const TABLE_WRAPPER_DIMMED_SX: SxProps<Theme> = { opacity: 0.7 };

const BODY_SCROLL_FILL_HEIGHT_SX: SxProps<Theme> = {
  flex: "0 1 auto",
  minHeight: 0,
  overflowX: "hidden",
  overflowY: "auto",
};

export type TableViewProps<T, TEmptyGroupData = unknown> = {
  table: TanstackTable<T>;
  getGroupedNode?: (row: Row<T>) => ReactNode;
  getEmptyGroupNode?: (emptyGroup: EmptyGroupMeta<TEmptyGroupData>) => ReactNode;
  emptyGroups?: EmptyGroupMeta<TEmptyGroupData>[];
  grouping?: GroupingState;
  sortingParams: SortingState;
  isLoading: boolean;
  isMultiSort: boolean;
  isDraggable?: boolean;
  draggableId?: string;   tableId?: string;   styles?: TableStyles;
  pinPrimaryColumnLeft?: boolean;
  fillHeight?: boolean;
  emptyMessage?: string;
  emptyState?: ReactNode;
  skeletonRowCount?: number;
};

export const TableView = <T, TEmptyGroupData = unknown>({
  table,
  getGroupedNode,
  getEmptyGroupNode,
  emptyGroups = [],
  sortingParams,
  isLoading,
  isMultiSort,
  isDraggable = false,
  draggableId,
  tableId,
  styles,
  pinPrimaryColumnLeft = false,
  fillHeight = false,
  emptyMessage,
  emptyState,
  skeletonRowCount = DEFAULT_SKELETON_ROW_COUNT,
}: TableViewProps<T, TEmptyGroupData>) => {
  const {
    leftColumns,
    rightColumns,
    renderSpacer,
    minTableWidth,
    getLeftColWidth,
    getRightColWidth,
  } = useTableLayout({ table, pinPrimaryColumnLeft, isDraggable });

  const rows = table.getExpandedRowModel().rows;
  const showOverlayLoader = isLoading && rows.length > 0;
  const tableStyle = { minWidth: `${minTableWidth}px` };

  const columnsGroup = (
    <TableColumnsGroup
      isDraggable={isDraggable}
      renderSpacer={renderSpacer}
      leftColumns={leftColumns}
      rightColumns={rightColumns}
      getLeftColWidth={getLeftColWidth}
      getRightColWidth={getRightColWidth}
    />
  );

  return (
    <Box
      className={styles?.containerClassName}
      sx={mergeSx(CONTAINER_SX, fillHeight ? CONTAINER_FILL_HEIGHT_SX : undefined)}>
      <Box
        sx={mergeSx(
          TABLE_WRAPPER_SX,
          fillHeight ? TABLE_WRAPPER_FILL_HEIGHT_SX : undefined,
          showOverlayLoader ? TABLE_WRAPPER_DIMMED_SX : undefined,
        )}
        style={fillHeight ? { minWidth: `${minTableWidth}px` } : undefined}>
        <MuiTable className="w-full shrink-0" style={tableStyle} sx={fixedTableSx}>
          {columnsGroup}

          <TableHead className="bg-gray-100">
            {table.getHeaderGroups().map((headerGroup: HeaderGroup<T>) => {
              const leftHeaders = headerGroup.headers.filter(
                (header) => !header.column.columnDef.meta?.isPinnedRight,
              );
              const rightHeaders = headerGroup.headers.filter(
                (header) => header.column.columnDef.meta?.isPinnedRight,
              );

              return (
                <TableRow key={headerGroup.id}>
                  {isDraggable && (
                    <TableCell component="th" scope="col" variant="head" className="w-8 py-3" />
                  )}

                  {leftHeaders.map((header) => (
                    <TableHeaderCell
                      key={header.id}
                      header={header}
                      sortingParams={sortingParams}
                      isMultiSort={isMultiSort}
                    />
                  ))}

                  {renderSpacer && (
                    <TableCell component="th" scope="col" variant="head" className="py-3" />
                  )}

                  {rightHeaders.map((header) => (
                    <TableHeaderCell
                      key={header.id}
                      header={header}
                      sortingParams={sortingParams}
                      isMultiSort={isMultiSort}
                    />
                  ))}
                </TableRow>
              );
            })}
          </TableHead>
        </MuiTable>

        <Box
          aria-live="polite"
          className={fillHeight ? "custom-scroll" : undefined}
          sx={fillHeight ? BODY_SCROLL_FILL_HEIGHT_SX : undefined}>
          <MuiTable className="w-full" style={tableStyle} sx={fixedTableSx}>
            {columnsGroup}

            <TableBody className="bg-white">
              <TableBodyContent
                rows={rows}
                emptyGroups={emptyGroups}
                getGroupedNode={getGroupedNode}
                getEmptyGroupNode={getEmptyGroupNode}
                isDraggable={isDraggable}
                draggableId={draggableId}
                tableId={tableId}
                renderSpacer={renderSpacer}
                leftColumnCount={leftColumns.length}
                rightColumnCount={rightColumns.length}
                styles={styles}
                isLoading={isLoading}
                emptyMessage={emptyMessage}
                emptyState={emptyState}
                skeletonRowCount={skeletonRowCount}
              />
            </TableBody>
          </MuiTable>
        </Box>
      </Box>

      <TableLoader isLoading={showOverlayLoader} />
    </Box>
  );
};
